import React, { useState, useEffect, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Divider } from "primereact/divider";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { InputTextarea } from 'primereact/inputtextarea';

import "./DialogCostos.scss";


/**
 * Formatea un valor numérico como moneda MXN
 */
const formatMXN = (valor) => {
    if (valor === null || valor === undefined) return "—";
    return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
    }).format(valor);
};

/**
 * Calcula automáticamente el pago subcontratista como 80% del precio cliente.
 * Ya no es editable desde la vista: siempre se deriva del precio al cliente.
 */
const calcularSubcontratista = (precioCliente) => {
    if (!precioCliente) return null;
    return parseFloat((precioCliente * 0.8).toFixed(2));
};

const DialogCostos = ({
                          visible = false,
                          observaciones,
                          onHide,
                          ruta = null,
                          onGuardar,
                          guardando = false,
                          user = null,
                      }) => {
    const [valores, setValores] = useState({
        pagoChofer: null,
        pagoChoferSencillo: null,
        precioCliente: null,
        pagoSubcontratista: null,
        observaciones: null,
    });

    const [errores, setErrores] = useState({});


    /**
     * Campos de costo con su metadata para renderizado dinámico.
     * pagoSubcontratista no está aquí: se calcula siempre como 80% del
     * precio cliente y ya no se muestra ni edita en la vista.
     */
    const CAMPOS_COSTO = [
        {
            key: "precioCliente",
            label: "Precio al Cliente",
            descripcion: "Tarifa que se cobra al cliente por el servicio",
            icon: "pi pi-building",
            color: "#10b981",
            required: user === 2,
        },
        {
            key: "pagoChofer",
            label: "Pago Chofer",
            descripcion: "Costo por servicio completo al operador",
            icon: "pi pi-user",
            color: "#3b82f6",
            required: user === 1,
            sugerenciaCampo: "pagoCorrespondiente",
        },
        {
            key: "pagoChoferSencillo",
            label: "Pago Chofer Sencillo",
            descripcion: "Costo de viaje sencillo (ida o regreso)",
            icon: "pi pi-arrow-right",
            color: "#8b5cf6",
            required: user === 1,
            sugerenciaCampo: "pagoCorrespondienteS",
        },
    ];


    // Inicializar con valores existentes de la ruta
    useEffect(() => {
        if (visible && ruta) {
            const precioInicial = ruta.precioCliente ?? null;
            const iniciales = {
                pagoChofer:         ruta.pagoChofer         ?? null,
                pagoChoferSencillo: ruta.pagoChoferSencillo ?? null,
                precioCliente:      precioInicial,
                pagoSubcontratista: calcularSubcontratista(precioInicial),
                observaciones:      observaciones           ?? null,
            };
            setValores(iniciales);
            setErrores({});
        }
    }, [visible, ruta]);

    const handleChange = useCallback((key, value) => {
        setValores((prev) => {
            const next = { ...prev, [key]: value };

            // El pago al aliado siempre se recalcula junto con el precio cliente
            if (key === "precioCliente") {
                next.pagoSubcontratista = calcularSubcontratista(value);
            }

            return next;
        });

        // Limpiar error al editar
        if (errores[key]) {
            setErrores((prev) => ({ ...prev, [key]: null }));
        }
    }, [errores]);

    const validar = () => {
        const nuevosErrores = {};
        camposFiltrados.forEach(({ key, label, required }) => {
            if (required && (valores[key] === null || valores[key] === undefined || valores[key] === "")) {
                nuevosErrores[key] = `${label} es requerido`;
            }
        });
        setErrores(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const handleGuardar = () => {
        if (!validar()) return;
        onGuardar?.({ rutaId: ruta?.id, ...valores });
    };

    const hayValoresIniciales = ruta?.precioCliente || ruta?.pagoChofer;

    const CAMPOS_VISIBLES_USER_2 = ["precioCliente"];

    const camposFiltrados = user === 2
        ? CAMPOS_COSTO.filter(c => CAMPOS_VISIBLES_USER_2.includes(c.key))
        : CAMPOS_COSTO;


    // ─── Footer ──────────────────────────────────────────────────────────────
    const footer = (
        <div className="dialog-costos__footer">
            <Button
                label="Cancelar"
                icon="pi pi-times"
                className="p-button-text p-button-plain"
                onClick={onHide}
                disabled={guardando}
            />
            <Button
                label={guardando ? "Guardando..." : "Guardar costos"}
                icon={guardando ? "pi pi-spin pi-spinner" : "pi pi-check"}
                className="p-button-success"
                onClick={handleGuardar}
                disabled={guardando}
            />
        </div>
    );

    // ─── Header ──────────────────────────────────────────────────────────────
    const header = (
        <div className="dialog-costos__header">
            <div className="dialog-costos__header-icon">
                <i className="pi pi-dollar" />
            </div>
            <div className="dialog-costos__header-text">
                <span className="dialog-costos__header-title">Definir Costos y Precios</span>
                {ruta && (
                    <span className="dialog-costos__header-subtitle">
                        <i className="pi pi-map-marker mr-1" />
                        {ruta.nombre}
                    </span>
                )}
            </div>
            {hayValoresIniciales && (
                <Tag value="Editando" icon="pi pi-pencil" severity="warning" className="ml-auto" />
            )}
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={header}
            footer={footer}
            className="dialog-costos"
            style={{ width: "560px" }}
            breakpoints={{ "768px": "95vw" }}
            scrollable
            contentStyle={{ maxHeight: "65vh", overflowY: "auto", padding: 0 }}
            modal
            draggable={false}
            resizable={false}
        >
            <div className="dialog-costos__body">

                {/* ── Aviso si es primera vez ── */}
                {!hayValoresIniciales && (
                    <Message
                        severity="info"
                        className="w-full mb-4"
                        content={
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-info-circle text-lg" />
                                <span>
                                    Esta ruta aún no tiene costos registrados. Los campos marcados con{" "}
                                    <strong>*</strong> son obligatorios.
                                </span>
                            </div>
                        }
                    />
                )}

                {/* ── Campos de costo ── */}
                <div className="dialog-costos__campos">
                    {camposFiltrados.map(({ key, label, descripcion, icon, color, required, sugerenciaCampo }) => {
                        const campoDeshabilitado = user === 1 && key === "precioCliente";
                        const sugerencia = sugerenciaCampo ? ruta?.[sugerenciaCampo] : null;

                        return (
                            <div
                                key={key}
                                className={`dialog-costos__campo ${errores[key] ? "dialog-costos__campo--error" : ""}`}
                            >
                                {/* Label */}
                                <div className="dialog-costos__campo-label">
                                    <div className="dialog-costos__campo-icon" style={{ background: `${color}18`, color }}>
                                        <i className={icon} />
                                    </div>
                                    <div>
                                        <span className="dialog-costos__campo-nombre">
                                            {label}
                                            {required && <span className="dialog-costos__required"> *</span>}
                                        </span>
                                        <span className="dialog-costos__campo-desc">{descripcion}</span>
                                    </div>
                                </div>

                                {/* Input */}
                                <InputNumber
                                    value={valores[key]}
                                    onValueChange={(e) => handleChange(key, e.value)}
                                    mode="currency"
                                    currency="MXN"
                                    locale="es-MX"
                                    placeholder="$0.00"
                                    disabled={campoDeshabilitado || guardando}
                                    className={`dialog-costos__input ${campoDeshabilitado ? "dialog-costos__input--locked" : ""}`}
                                    min={0}
                                    maxFractionDigits={2}
                                />

                                {sugerencia != null && (
                                    <small className="dialog-costos__sugerencia">
                                        <i className="pi pi-info-circle mr-1" />
                                        El pago al operador debería ser {formatMXN(sugerencia)}
                                    </small>
                                )}

                                {errores[key] && (
                                    <small className="dialog-costos__error-msg">
                                        <i className="pi pi-exclamation-triangle mr-1" />
                                        {errores[key]}
                                    </small>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ── Observaciones ── */}
                <div className="dialog-costos__campo mt-4">
                    <div className="dialog-costos__campo-label">
                        <div className="dialog-costos__campo-icon" style={{ background: '#6b728018', color: '#6b7280' }}>
                            <i className="pi pi-comment" />
                        </div>
                        <div>
                            <span className="dialog-costos__campo-nombre">Observaciones</span>
                            <span className="dialog-costos__campo-desc">Notas adicionales sobre los costos</span>
                        </div>
                    </div>
                    <InputTextarea
                        value={valores.observaciones ?? ''}
                        onChange={(e) => handleChange('observaciones', e.target.value)}
                        rows={3}
                        autoResize
                        placeholder="Escribe observaciones..."
                        disabled={guardando}
                        className="dialog-costos__input w-full"
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default DialogCostos;
